import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import clientService from '../../services/clientService';
import ClientFilters from '../../components/clients/ClientFilters';
import DataTable, { Column } from '../../components/common/DataTable';
import { Cliente } from '@/types';
import { getClientFullName } from '@/utils/clientUtils';
import { useAuth } from '@/contexts/AuthContext';

const ClientList: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [result, setResult] = useState<{ items: Cliente[]; total: number; page: number; totalPages: number }>({
    items: [], total: 0, page: 1, totalPages: 1
  });
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState('');
  const [estado, setEstado] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const skipSearchEffect = useRef(true);

  const load = async (
    p = 1,
    searchTerm = search,
    filters: { tipo?: string; estado?: string; sortColumn?: string | null; sortDirection?: 'asc' | 'desc' } = {}
  ) => {
    setLoading(true);
    try {
      const res: any = await clientService.getClients({
        tipo: (filters.tipo ?? tipo) || undefined,
        estado: (filters.estado ?? estado) || undefined,
        search: searchTerm.trim() || undefined,
        page: p,
        limit: 25,
        ...((filters.sortColumn ?? sortColumn) ? { sort: filters.sortColumn ?? sortColumn, dir: filters.sortDirection ?? sortDirection } : {})
      });
      setResult(res.data);
      setPage(res.data.page);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  useEffect(() => {
    if (skipSearchEffect.current) {
      skipSearchEffect.current = false;
      return;
    }

    const timeout = window.setTimeout(() => load(1, search), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    load(1, search);
  }, [tipo, estado, sortColumn, sortDirection]);

  const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const columns: Column<Cliente>[] = [
    {
      key: 'documento',
      label: 'Documento',
      sortable: true,
      render: (client) => `${client.tipo_documento} ${client.documento}`
    },
    {
      key: 'nombre',
      label: 'Nombre',
      sortable: true,
      render: (client) => getClientFullName(client)
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      render: (client) => client.email || '-'
    },
    {
      key: 'telefono',
      label: 'Teléfono',
      render: (client) => client.telefono || '-'
    },
    {
      key: 'tipo',
      label: 'Tipo',
      sortable: true,
      render: (client) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {client.tipo}
        </span>
      )
    },
    {
      key: 'activo',
      label: 'Estado',
      sortable: true,
      render: (client) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          client.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {client.activo ? 'Activo' : 'Inactivo'}
        </span>
      )
    }
  ];

  const handleRowClick = (client: Cliente) => {
    navigate(`/clients/${client.id}`);
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clientes</h1>
            <p className="text-gray-600 mt-1">Gestión de propietarios e inquilinos</p>
          </div>
          <div className="flex gap-2">
            <ClientFilters
              tipo={tipo}
              estado={estado}
              onChange={(v) => {
                const nextTipo = v.tipo !== undefined ? v.tipo! : tipo;
                const nextEstado = v.estado !== undefined ? v.estado! : estado;
                if (v.tipo !== undefined) setTipo(nextTipo);
                if (v.estado !== undefined) setEstado(nextEstado);
                load(1, search, { tipo: nextTipo, estado: nextEstado });
              }}
              onApply={() => load(1, search)}
            />
            {hasPermission('clients', 'write') && (
              <button
                onClick={() => navigate('/clients/new')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Nuevo Cliente
              </button>
            )}
          </div>
        </div>

        <DataTable
          columns={columns}
          data={result.items}
          loading={loading}
          searchable={true}
          serverSearch={true}
          serverSort={true}
          searchValue={search}
          onSearchChange={setSearch}
          sortValue={{ column: sortColumn, direction: sortDirection }}
          onSortChange={handleSortChange}
          searchPlaceholder="Buscar por nombre, documento, email..."
          onRowClick={handleRowClick}
          rowKey="id"
          emptyState={
            <div className="text-center py-12">
              <div className="text-6xl mb-4">👥</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay clientes registrados</h3>
              <p className="text-gray-600 mb-4">Comienza agregando tu primer cliente</p>
              {hasPermission('clients', 'write') && (
                <button
                  onClick={() => navigate('/clients/new')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Agregar Cliente
                </button>
              )}
            </div>
          }
        />

        {/* Pagination */}
        {result.items.length > 0 && (
          <div className="mt-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Mostrando {((page - 1) * 25) + 1} a {Math.min(page * 25, result.total)} de {result.total} clientes
            </div>
            <div className="flex gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page <= 1}
                onClick={() => load(page - 1, search)}
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-gray-700">
                Página {page} de {result.totalPages}
              </span>
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page >= result.totalPages}
                onClick={() => load(page + 1, search)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default ClientList;


